import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Package, Warehouse, ShoppingCart, ClipboardList, AlertTriangle, Truck,
  UserCog, ShieldCheck, Camera, FileSearch, ChevronRight, Receipt,
  Users, CalendarClock, ListChecks, FileText, Wrench, ClipboardCheck,
  RotateCcw, BookOpen, FileBarChart,
} from 'lucide-react';

const ROLE_CONFIG = {
  TECHNICIAN: {
    title: 'Technician Workbench',
    subtitle: 'Complaints assigned to you, work orders, PPM tasks & material returns',
    accent: 'sky',
    kpis: [
      { key: 'complaint', label: 'My active complaints', taskType: 'complaint', Icon: AlertTriangle, color: 'amber', link: '/complaints?scope=mine' },
      { key: 'wo',        label: 'My work orders',       taskType: 'work_order', Icon: ClipboardCheck, color: 'sky', link: '/work-orders?scope=mine' },
      { key: 'ppm',       label: 'My PPM tasks',         taskType: 'ppm',        Icon: ClipboardList,  color: 'violet', link: '/ppm-tasks' },
    ],
    quickActions: [
      { label: 'My Complaints',   icon: AlertTriangle,  link: '/complaints?scope=mine' },
      { label: 'My Work Orders',  icon: ClipboardCheck, link: '/work-orders?scope=mine' },
      { label: 'New Service Report', icon: FileBarChart, link: '/reports' },
      { label: 'Scan QR / Asset', icon: Camera,         link: '/scan' },
      { label: 'PPM Tasks',       icon: ClipboardList,  link: '/ppm-tasks' },
      { label: 'PPM Calendar',    icon: CalendarClock,  link: '/ppm-calendar' },
      { label: 'Assets',          icon: Wrench,         link: '/assets' },
      { label: 'Pending Returns', icon: RotateCcw,      link: '/pending-returns' },
      { label: 'SOPs & SLAs',     icon: BookOpen,       link: '/sops' },
    ],
  },
  CS_SUPERVISOR: {
    title: 'Customer Service Supervisor',
    subtitle: 'Triage complaints, assign technicians, approve materials & closure',
    accent: 'rose',
    kpis: [
      { key: 'complaint',  label: 'Open complaints',       taskType: 'complaint',  Icon: AlertTriangle, color: 'rose', link: '/complaints?status=open' },
      { key: 'inspection', label: 'Pending inspections',   taskType: 'inspection', Icon: FileSearch,    color: 'violet', link: '/inspections' },
      { key: 'wo',         label: 'Work orders in flight', taskType: 'work_order', Icon: ClipboardCheck, color: 'sky', link: '/work-orders' },
    ],
    quickActions: [
      { label: 'Complaints',     icon: AlertTriangle,  link: '/complaints' },
      { label: 'Work Orders',    icon: ClipboardCheck, link: '/work-orders' },
      { label: 'Service Reports',icon: FileBarChart,   link: '/reports' },
      { label: 'Inspections',    icon: FileSearch,     link: '/inspections' },
      { label: 'PPM Tasks',      icon: ClipboardList,  link: '/ppm-tasks' },
      { label: 'Housekeeping',   icon: ShieldCheck,    link: '/housekeeping' },
    ],
  },
  ENGINEER: {
    title: 'Engineer / Supervisor Desk',
    subtitle: 'Assign complaints, approve materials, approve service reports & close complaints',
    accent: 'sky',
    kpis: [
      { key: 'complaint', label: 'Complaints to triage',   taskType: 'complaint',  Icon: AlertTriangle,  color: 'amber', link: '/complaints' },
      { key: 'wo',        label: 'Work orders in flight',  taskType: 'work_order', Icon: ClipboardCheck, color: 'sky',   link: '/work-orders' },
      { key: 'task',      label: 'My pending approvals',   taskType: 'all',        Icon: ListChecks,     color: 'violet', link: '#tasks' },
    ],
    quickActions: [
      { label: 'Complaints',         icon: AlertTriangle,  link: '/complaints' },
      { label: 'Service Reports',    icon: FileBarChart,   link: '/reports' },
      { label: 'Work Orders',        icon: ClipboardCheck, link: '/work-orders' },
      { label: 'PPM Tasks',          icon: ClipboardList,  link: '/ppm-tasks' },
      { label: 'PPM Calendar',       icon: CalendarClock,  link: '/ppm-calendar' },
      { label: 'Assets',             icon: Wrench,         link: '/assets' },
      { label: 'Pending Returns',    icon: RotateCcw,      link: '/pending-returns' },
      { label: 'Technician KPI',     icon: FileBarChart,   link: '/technician-kpi' },
    ],
  },
  ACCOUNTANT: {
    title: 'Finance & Accounting',
    subtitle: 'Invoices, payments, payroll & reconciliation',
    accent: 'emerald',
    kpis: [
      { key: 'invoice', label: 'Bills to clear',      taskType: 'invoice',  Icon: Receipt,   color: 'rose',    link: '/accounting' },
      { key: 'wo',      label: 'WOs to bill',          taskType: 'work_order', Icon: ClipboardCheck, color: 'sky', link: '/work-orders' },
      { key: 'task',    label: 'My pending tasks',    taskType: 'all',      Icon: ListChecks, color: 'emerald', link: '#tasks' },
    ],
    quickActions: [
      { label: 'Accounting',     icon: Receipt,      link: '/accounting' },
      { label: 'Purchases',      icon: ShoppingCart, link: '/purchases' },
      { label: 'Payments',       icon: Truck,        link: '/procurement' },
      { label: 'Payroll',        icon: UserCog,      link: '/hr' },
      { label: 'Custom Reports', icon: FileText,     link: '/custom-reports' },
    ],
  },
  STOREKEEPER: {
    title: 'Storekeeper Workbench',
    subtitle: 'Stock movements, GRN, material issuance & returns',
    accent: 'amber',
    kpis: [
      { key: 'stock', label: 'Low-stock alerts',       taskType: 'stock',       Icon: Warehouse,    color: 'amber',   link: '/stock' },
      { key: 'mr',    label: 'Material requests',      taskType: 'material_request', Icon: Package, color: 'purple',  link: '/material-requests' },
      { key: 'ret',   label: 'Pending returns',        taskType: 'all',         Icon: RotateCcw,    color: 'emerald', link: '/pending-returns' },
    ],
    quickActions: [
      { label: 'Stock',              icon: Warehouse,   link: '/stock' },
      { label: 'Material Requests',  icon: Package,     link: '/material-requests' },
      { label: 'Pending Returns',    icon: RotateCcw,   link: '/pending-returns' },
      { label: 'Stock Audit',        icon: FileSearch,  link: '/stock-movements' },
      { label: 'Goods Receipt (GRN)', icon: Truck,      link: '/procurement' },
      { label: 'Suppliers',          icon: Package,     link: '/suppliers' },
    ],
  },
  PROCUREMENT_OFFICER: {
    title: 'Procurement Office',
    subtitle: 'LPOs, supplier management, RFQs & approvals',
    accent: 'violet',
    kpis: [
      { key: 'po', label: 'Pending LPOs', taskType: 'work_order', Icon: ClipboardList, color: 'violet', link: '/purchase-orders' },
      { key: 'stock', label: 'Reorder alerts', taskType: 'stock', Icon: AlertTriangle, color: 'amber', link: '/stock' },
      { key: 'invoice', label: 'Bills to clear', taskType: 'invoice', Icon: Receipt, color: 'rose', link: '/accounting' },
    ],
    quickActions: [
      { label: 'New LPO', icon: ClipboardList, link: '/purchase-orders' },
      { label: 'Suppliers', icon: Package, link: '/suppliers' },
      { label: 'Quotations / RFQ', icon: FileText, link: '/quotations' },
      { label: 'Supplier Dashboard', icon: Truck, link: '/supplier-dashboard' },
    ],
  },
  HR_MANAGER: {
    title: 'HR Office',
    subtitle: 'People operations, attendance, leave & payroll',
    accent: 'rose',
    kpis: [
      { key: 'task', label: 'My pending tasks', taskType: 'all', Icon: ListChecks, color: 'rose', link: '#tasks' },
    ],
    quickActions: [
      { label: 'HR Module', icon: UserCog, link: '/hr' },
      { label: 'Staff', icon: Users, link: '/staff' },
      { label: 'Attendance', icon: CalendarClock, link: '/attendance' },
    ],
  },
  SECURITY_OFFICER: {
    title: 'Security Operations',
    subtitle: 'Patrol schedules, incidents, gate-pass & visitor logs',
    accent: 'indigo',
    kpis: [
      { key: 'schedule', label: 'Today\'s patrols', taskType: 'schedule', Icon: CalendarClock, color: 'indigo', link: '/schedules' },
      { key: 'complaint', label: 'Open security issues', taskType: 'complaint', Icon: AlertTriangle, color: 'rose', link: '/complaints' },
      { key: 'inspection', label: 'Site inspections', taskType: 'inspection', Icon: FileSearch, color: 'fuchsia', link: '/inspections' },
    ],
    quickActions: [
      { label: 'Patrol Schedules', icon: CalendarClock, link: '/schedules' },
      { label: 'Scan QR / Patrol Point', icon: Camera, link: '/scan' },
      { label: 'Incident Reports', icon: FileText, link: '/reports' },
      { label: 'Site Inspections', icon: ShieldCheck, link: '/inspections' },
    ],
  },
};

const ACCENT_CLASS = {
  amber:   { card: 'border-amber-200 dark:border-amber-700/40',   value: 'text-amber-700 dark:text-amber-300',   bg: 'bg-amber-50/40 dark:bg-amber-900/10' },
  sky:     { card: 'border-sky-200 dark:border-sky-700/40',       value: 'text-sky-700 dark:text-sky-300',       bg: 'bg-sky-50/40 dark:bg-sky-900/10' },
  emerald: { card: 'border-emerald-200 dark:border-emerald-700/40', value: 'text-emerald-700 dark:text-emerald-300', bg: 'bg-emerald-50/40 dark:bg-emerald-900/10' },
  violet:  { card: 'border-violet-200 dark:border-violet-700/40', value: 'text-violet-700 dark:text-violet-300', bg: 'bg-violet-50/40 dark:bg-violet-900/10' },
  rose:    { card: 'border-rose-200 dark:border-rose-700/40',     value: 'text-rose-700 dark:text-rose-300',     bg: 'bg-rose-50/40 dark:bg-rose-900/10' },
  indigo:  { card: 'border-indigo-200 dark:border-indigo-700/40', value: 'text-indigo-700 dark:text-indigo-300', bg: 'bg-indigo-50/40 dark:bg-indigo-900/10' },
  fuchsia: { card: 'border-fuchsia-200 dark:border-fuchsia-700/40', value: 'text-fuchsia-700 dark:text-fuchsia-300', bg: 'bg-fuchsia-50/40 dark:bg-fuchsia-900/10' },
};

export default function RoleDashboard({ role }) {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const cfg = ROLE_CONFIG[role];
  const [summary, setSummary] = useState({ total: 0, by_type: {}, critical: 0, overdue: 0 });
  const [recentTasks, setRecentTasks] = useState([]);
  const [mrSummary, setMrSummary] = useState({ pending: 0, ready_to_issue: 0 });

  useEffect(() => {
    axios.get(`${API}/my-tasks`, { withCredentials: true })
      .then(r => {
        setSummary(r.data?.summary || {});
        setRecentTasks((r.data?.tasks || []).slice(0, 6));
      })
      .catch(() => {});
    // Material-request summary — surfaced on the Storekeeper KPI tile.
    axios.get(`${API}/material-requests/summary/pending`, { withCredentials: true })
      .then(r => setMrSummary(r.data || {}))
      .catch(() => {});
  }, []);

  if (!cfg) return null;
  const acc = ACCENT_CLASS[cfg.accent] || ACCENT_CLASS.amber;

  return (
    <Layout>
      <div data-testid="role-dashboard" className="space-y-6">
        <div className={`p-5 rounded-xl ${acc.bg} border ${acc.card}`}>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{role.replace(/_/g, ' ')}</div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mt-1">{cfg.title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{cfg.subtitle}</p>
          <p className="text-xs text-muted-foreground mt-1">Welcome back, <strong className="text-foreground">{user?.name}</strong></p>
        </div>

        {/* Quick-action grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {cfg.quickActions.map((qa) => {
            const Ico = qa.icon;
            return (
              <button
                key={qa.link}
                onClick={() => navigate(qa.link)}
                data-testid={`role-quickaction-${qa.link.replace(/\//g, '-')}`}
                className="group p-4 rounded-xl border border-border bg-card hover:bg-muted transition text-left"
              >
                <Ico className={`w-6 h-6 ${acc.value} mb-2`} />
                <div className="text-sm font-semibold text-foreground">{qa.label}</div>
                <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-0.5">Open <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition" /></div>
              </button>
            );
          })}
        </div>

        {/* KPI counters from My Tasks */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {cfg.kpis.map((kpi) => {
            const KIco = kpi.Icon;
            const kcc = ACCENT_CLASS[kpi.color] || acc;
            let value;
            if (kpi.taskType === 'material_request') {
              value = (mrSummary.pending || 0) + (mrSummary.ready_to_issue || 0);
            } else if (kpi.taskType === 'all') {
              value = summary.total;
            } else {
              value = summary.by_type?.[kpi.taskType] || 0;
            }
            return (
              <Card key={kpi.key} data-testid={`role-kpi-${kpi.key}`} className={`p-4 border ${kcc.card}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{kpi.label}</div>
                    <div className={`text-3xl font-bold mt-1 ${kcc.value}`}>{value}</div>
                  </div>
                  <KIco className={`w-7 h-7 opacity-30 ${kcc.value}`} />
                </div>
                <Button
                  size="sm" variant="ghost" className="mt-2 -ml-2 text-xs"
                  onClick={() => navigate(kpi.link)}
                >
                  View all <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </Card>
            );
          })}
        </div>

        {/* My latest tasks (uses Phase 27 aggregator) */}
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-muted/40">
            <h2 className="font-semibold text-foreground flex items-center gap-2"><ListChecks className="w-4 h-4" /> Recent pending tasks for you</h2>
            <span className="text-xs text-muted-foreground">{summary.total || 0} total{(summary.overdue || 0) > 0 ? ` · ${summary.overdue} overdue` : ''}</span>
          </div>
          {recentTasks.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">All caught up — no pending tasks.</div>
          ) : (
            <ul className="divide-y divide-border">
              {recentTasks.map(t => (
                <li key={`${t.type}-${t.id}`} className="px-4 py-2.5 flex items-start gap-3 hover:bg-muted/30">
                  <div className="flex-1 min-w-0">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">{t.type.replace(/_/g, ' ')}</div>
                    <div className="font-medium text-foreground truncate">{t.title}</div>
                    {t.due_date && (
                      <div className={`text-xs mt-0.5 ${t.is_overdue ? 'text-rose-600 dark:text-rose-400 font-semibold' : 'text-muted-foreground'}`}>
                        Due {new Date(t.due_date).toLocaleDateString()}{t.is_overdue ? ' (overdue)' : ''}
                      </div>
                    )}
                  </div>
                  <Button size="sm" variant="outline" onClick={() => navigate(t.link)}>Open</Button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </Layout>
  );
}
